import React, { useState, useEffect } from 'react';
import { ViewLayout } from '../ui';
import addSampleCategories from '../../utils/addSampleCategories';
import migratePropsToCategories from '../../utils/migratePropsToCategories';
import { supabase } from '../../supabaseClient';

const DatabaseSetupView = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationError, setMigrationError] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  
  const [categoriesExist, setCategoriesExist] = useState(false);
  const [checkingCategories, setCheckingCategories] = useState(true);

  // Check if categories already exist on component mount
  useEffect(() => {
    checkExistingCategories();
  }, []);

  const checkExistingCategories = async () => {
    try {
      setCheckingCategories(true);
      const { data, error } = await supabase
        .from('categories')
        .select('id')
        .limit(1);
      
      if (error) throw error;
      setCategoriesExist(data && data.length > 0);
    } catch (err) {
      console.error('Error checking categories:', err);
      setCategoriesExist(false);
    } finally {
      setCheckingCategories(false);
    }
  };

  const handleAddCategories = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const setupResult = await addSampleCategories();
      setResult(setupResult);
      setCategoriesExist(true); // Update the state
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateProps = async () => {
    setMigrationLoading(true);
    setMigrationError(null);
    setMigrationResult(null);

    try {
      const migrationResult = await migratePropsToCategories();
      setMigrationResult(migrationResult);
    } catch (err) {
      setMigrationError(err.message);
    } finally {
      setMigrationLoading(false);
    }
  };

  const statsData = [
    { label: 'Categories', value: (result || categoriesExist) ? 'Complete' : 'Pending', color: (result || categoriesExist) ? 'green' : 'gray' },
    { label: 'Props Migration', value: migrationResult ? 'Complete' : 'Pending', color: migrationResult ? 'green' : 'gray' },
    { label: 'Total Categories', value: result?.totalCreated || (categoriesExist ? 'Existing' : 0), color: 'blue' },
    { label: 'Migrated Props', value: migrationResult?.migratedCount || 0, color: 'purple' }
  ];

  const stats = statsData.map((stat, index) => (
    <ViewLayout.Stat
      key={index}
      title={stat.label}
      value={stat.value}
      color={stat.color}
    />
  ));

  return (
    <ViewLayout
      title="Database Setup"
      subtitle="Initialize sample data for PropShare development"
      stats={stats}
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Category Setup Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Sample Categories</h3>
              <p className="text-sm text-gray-600">
                Add hierarchical category structure to the database
              </p>
            </div>
            <button
              onClick={handleAddCategories}
              disabled={loading || result || categoriesExist}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                loading || result || categoriesExist
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {checkingCategories ? 'Checking...' : loading ? 'Adding...' : (result || categoriesExist) ? 'Categories Exist ✓' : 'Add Sample Categories'}
            </button>
          </div>

          {/* Category Structure Preview */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Categories to be created:</h4>
            <div className="text-sm text-gray-700 space-y-2">
              <div><strong>Furniture</strong> → Seating → Chairs, Sofas & Couches, Benches, Stools</div>
              <div><strong>Costumes & Textiles</strong> → Period Costumes → Victorian Era, Medieval, 1920s-1950s</div>
              <div><strong>Hand Props</strong> → Kitchen & Dining → Dishes & Plates, Glassware, Cookware</div>
              <div><strong>Set Pieces</strong> → Architectural, Backdrops, Platforms, Nature Elements</div>
              <div><strong>Lighting & Effects</strong> → Stage Lighting, Practical Lights, Special Effects</div>
              <div><strong>Audio & Electronics</strong> → Sound Equipment, Electronics Props</div>
              <div><strong>Vehicles & Transport</strong> → Period Vehicles, Modern Transport</div>
              <div><strong>Weapons & Combat</strong> → Swords & Blades, Armor & Shields, Projectile Props</div>
            </div>
          </div>

          {/* Results Display */}
          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-5 h-5 text-green-600 mr-2">✓</div>
                <h4 className="text-sm font-medium text-green-900">Categories Added Successfully</h4>
              </div>
              <div className="text-sm text-green-700 space-y-1">
                <div>• {result.rootCategories} root categories</div>
                <div>• {result.subcategories} subcategories</div>
                <div>• {result.thirdLevel} third-level categories</div>
                <div className="font-medium mt-2">Total: {result.totalCreated} categories</div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-5 h-5 text-red-600 mr-2">×</div>
                <h4 className="text-sm font-medium text-red-900">Error Adding Categories</h4>
              </div>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Props Migration Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Props Migration</h3>
              <p className="text-sm text-gray-600">
                Migrate existing props to use the new category system
              </p>
            </div>
            <button
              onClick={handleMigrateProps}
              disabled={migrationLoading || migrationResult || (!result && !categoriesExist)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                migrationLoading || migrationResult || (!result && !categoriesExist)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {migrationLoading ? 'Migrating...' : migrationResult ? 'Migration Complete ✓' : (!result && !categoriesExist) ? 'Add Categories First' : 'Migrate Props'}
            </button>
          </div>

          {/* Migration Description */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Migration Process:</h4>
            <div className="text-sm text-gray-700 space-y-2">
              <div>1. Add category_id column to props table</div>
              <div>2. Match existing category strings to new categories</div>
              <div>3. Update props with proper category relationships</div>
              <div>4. Create database indexes for performance</div>
            </div>
          </div>

          {/* Migration Results */}
          {migrationResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-5 h-5 text-green-600 mr-2">✓</div>
                <h4 className="text-sm font-medium text-green-900">Props Migration Complete</h4>
              </div>
              <div className="text-sm text-green-700 space-y-1">
                <div>• {migrationResult.totalProps} props analyzed</div>
                <div>• {migrationResult.migratedCount} props successfully migrated</div>
                {migrationResult.failedCount > 0 && (
                  <div className="text-orange-700">• {migrationResult.failedCount} props used default category</div>
                )}
              </div>
            </div>
          )}

          {migrationError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-5 h-5 text-red-600 mr-2">×</div>
                <h4 className="text-sm font-medium text-red-900">Migration Error</h4>
              </div>
              <p className="text-sm text-red-700">{migrationError}</p>
            </div>
          )}
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Next Steps</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full mr-3 ${(result || categoriesExist) ? 'bg-green-400' : 'bg-blue-400'}`}></div>
              {(result || categoriesExist) ? '✓ Categories exist in database' : 'Add sample categories'}
            </div>
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full mr-3 ${migrationResult ? 'bg-green-400' : 'bg-blue-400'}`}></div>
              {migrationResult ? '✓ Props migrated successfully' : 'Migrate props to new category system'}
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
              Create CategoryManagementView component
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
              Update SearchView to use new category system
            </div>
          </div>
        </div>
      </div>
    </ViewLayout>
  );
};

export default DatabaseSetupView;
